import React, {useEffect} from "react";
import '../../../../styles/components/onboarding/keyboard-setup/connect-your-keyboard-page.scss';
import Button from "../../../common/Button";
import onboarding from "../../../../services/onboarding/onboarding";
import SelectWithVisibleOptions from "../../../common/SelectWithVisibleOptions";
import ExternalLink from "../../../common/ExternalLink";
import WebPImage from "../../../common/WebPImage";
import {appPagePaths, onboardingImagesSource} from "../../../../config/config";
import BackButton from "../../../common/BackButton";
import {keyboardWizardStore} from "../../../../redux/keyboardWizard/keyboardWizardStore";
import {KeyboardWizardMode, KeyboardWizardPage} from "../../../../redux/keyboardWizard/keyboardWizardTypes";
import {useAppSelector} from "../../../../redux/hooks";
import ISelectOption from "../../../../models/view/common/ISelectOption";
import {settingsStore} from "../../../../redux/settings/settingsStore";
import {useNavigate} from "react-router-dom";
import {IKeyboardConnectionHelpProps, useKeyboardConnectionHelp} from "../useKeyboardConnectionHelp.hook";

interface IChooseYourKeyboardProps {
  mode: KeyboardWizardMode
}

export default function ChooseYourKeyboard({mode}: IChooseYourKeyboardProps) {
  const midiDeviceSelectOptions = useAppSelector(() => { return settingsStore.getMidiDeviceOptions().getSelectOptions(); });
  const deviceToConfigureId = useAppSelector(keyboardWizardStore.getDeviceToConfigureId);
  const selectedMidiDeviceId = getIdOfSelectedOption(midiDeviceSelectOptions, deviceToConfigureId);
  const deviceValues = useKeyboardConnectionHelp();

  useEffect(() => {
    if (selectedMidiDeviceId !== undefined) {
      const selectedOption = midiDeviceSelectOptions.find((device) => device.value === selectedMidiDeviceId);
      if (selectedOption) {
        keyboardWizardStore.setDeviceToConfigureName(selectedOption.label);
        keyboardWizardStore.setDeviceToConfigureId(selectedOption.value);
      }
    }
  }, [selectedMidiDeviceId, midiDeviceSelectOptions]);

  const navigate = useNavigate();

  const contentMessage = createContentMessage(midiDeviceSelectOptions.length, deviceValues);
  const navigationButton = createNavigationButton(midiDeviceSelectOptions.length);

  const onClickBackButton = () => { mode === "fullscreen" ? navigate(appPagePaths.setupYourKeyboardPage) : keyboardWizardStore.setIsOpen(false); };

  return (
    <div className="connect-your-keyboard-page">
      <header className="top">
        <BackButton onClick={onClickBackButton}/>
        <WebPImage height={237} width={972} additionalClassName="keyboard-image" src={onboardingImagesSource.keyboardWithoutHighlights} />
        <div className="overlay-text">
          <h1>Set up keyboard</h1>
        </div>
      </header>
      <div className="content-and-navigation">
        <div className="content">
          {contentMessage}
          <SelectWithVisibleOptions
            onChange={(deviceId, deviceName) => {
              keyboardWizardStore.setDeviceToConfigureId(deviceId);
              keyboardWizardStore.setDeviceToConfigureName(deviceName);
            }}
            options={midiDeviceSelectOptions}
            value={selectedMidiDeviceId}
          />
          <div className="need-help-connecting">
            <ExternalLink url={deviceValues.supportLink} title={"Keyboard Connection Help"}/>
          </div>
        </div>
        <div className="navigation">
          {navigationButton}
        </div>
      </div>
    </div>
  );
}

function getIdOfSelectedOption(deviceOptions: ISelectOption<string>[], deviceToConfigureId: string | undefined): string | undefined {
  // If device to configure is not selected already in the keyboard wizard, try to select the device from settings.
  // If that doesn't work, auto-select the first option.
  if (deviceToConfigureId !== undefined) {
    return deviceToConfigureId;
  }

  const selectedMidiDeviceIdFromSettings = settingsStore.getSelectedMidiDeviceId();
  const filteredDevices = deviceOptions.filter((device) => { return device.value === selectedMidiDeviceIdFromSettings; });

  if (filteredDevices.length === 1) {
    return selectedMidiDeviceIdFromSettings;
  }

  if (deviceOptions.length > 0) {
    return deviceOptions[0].value;
  }

  return undefined;
}

function createNavigationButton(numberOfKeyboardsDetected: number){
  const setupKeyboardLaterButton = createSetupKeyboardLaterButton();
  const nextButton = createNextButton();
  return numberOfKeyboardsDetected === 0 ? setupKeyboardLaterButton : nextButton;
}

function createSetupKeyboardLaterButton() {
  return <Button
    buttonType="primary"
    onClick={()=>{onboarding.setupKeyboardLater()}}
    text="Set Up Keyboard Later"
  />;
}

function createNextButton() {
  return <Button
    buttonType="secondary"
    onClick={()=>{
      keyboardWizardStore.setCurrentPage(KeyboardWizardPage.SetLowestNote);
    }}
    text="Next"
  />
}

function createContentMessage( numberOfMidiDevicesDetected: number, deviceValues: IKeyboardConnectionHelpProps) {
  if (numberOfMidiDevicesDetected <= 0) {
    return (
      <div className="to-connect-instructions">
        <div className="connect-header">
          To connect, plug in the USB/MIDI cables required for your keyboard and {deviceValues.device}.
        </div>
      </div>
    );
  }

  if (numberOfMidiDevicesDetected === 1) {
    return <div className="select-instructions">Is this the device you would like to use?</div>
  }

  return <div className="select-instructions">Which device would you like to use?</div>
}
